import React, { useState, useEffect } from 'react';
import { progressService } from '../../../services/progressService';
import { courseService } from '../../../services/courseService';

function DashboardContent() {
    const [dashboardStats, setDashboardStats] = useState(null);
    const [moduleProgress, setModuleProgress] = useState([]);
    const [loading, setLoading] = useState(true);
    const [courseId, setCourseId] = useState(null);
    const [showAllModules, setShowAllModules] = useState(false);


    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            
            const statsResult = await progressService.getDashboardStats();
            if (statsResult.success) {
                setDashboardStats(statsResult.data);
            }

            const coursesResult = await courseService.getCourses();
            if (coursesResult.success && coursesResult.data.courses.length > 0) {
                const firstCourse = coursesResult.data.courses[0];
                setCourseId(firstCourse.id);

                const progressResult = await progressService.getCourseProgress(firstCourse.id);
                if (progressResult.success) {
                    const sortedModules = (progressResult.data.modules || []).sort((a, b) => {
                        const orderA = a.order || 0;
                        const orderB = b.order || 0;
                        return orderA - orderB;
                    });
                    setModuleProgress(sortedModules);
                }
            }
        } catch (error) {
            console.error('Failed to load dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getCompletedLessons = () => {
        if (!dashboardStats?.progressStats) return 0;
        const completed = dashboardStats.progressStats.find((stat) => stat.status === 'COMPLETED');
        return completed?._count || 0;
    };

    const getCompletedModulesCount = () => {
        if (!moduleProgress.length) return 0;
        return moduleProgress.filter((module) => module.progressPercentage === 100).length;
    };

    if (loading) {
        return (
            <div className="bg-[#E2F2FF] rounded-xl p-3 lg:p-3 shadow-md border border-blue-200">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-center py-16">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#E2F2FF] rounded-xl p-3 lg:p-3 shadow-md border border-blue-200">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
                    {/* Left Column */}
                    <div className="lg:col-span-2 space-y-4 lg:space-y-6">
                        {/* Learning Status */}
                        <div>
                            <h2 className="text-base lg:text-lg font-semibold text-gray-700 mb-3 lg:mb-4">Learning Status</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                                {/* Completed Modules */}
                                <div className="bg-primary text-white rounded-xl p-5 lg:p-6 text-center shadow-lg border border-blue-600">
                                    <div className="rounded-lg inline-block mb-3">
                                        <svg className="w-10 h-10 lg:w-12 lg:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                        </svg>
                                    </div>
                                    <div className="bg-white text-black rounded-lg p-3">
                                        <div className="text-2xl font-bold mb-2 text-gray-700">Completed Modules</div>
                                        <div className="text-4xl lg:text-4xl font-bold">{getCompletedModulesCount()}</div>
                                    </div>
                                </div>
                                {/* Passed Lessons */}
                                <div className="bg-gradient-to-br from-[#52a85e] to-green-700 text-white rounded-xl p-5 lg:p-6 text-center shadow-lg border border-green-600">
                                    <div className="rounded-lg inline-block mb-3">
                                        <svg className="w-10 h-10 lg:w-12 lg:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                        </svg>
                                    </div>
                                    <div className="bg-white text-black rounded-lg p-3">
                                        <div className="text-2xl font-bold mb-2 text-gray-700">Completed Lessons</div>
                                        <div className="text-4xl lg:text-4xl font-bold">{getCompletedLessons()}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Module Progress Bar Chart */}
                        <div>
                            <h2 className="text-base lg:text-lg font-semibold text-gray-700 mb-3 lg:mb-4">Module Progress</h2>
                            <div className="bg-white rounded-xl p-4 lg:p-6 shadow-md border border-gray-200 overflow-x-auto">
                                <div className="min-w-[600px] lg:min-w-0">
                                    <div className="flex items-end justify-between gap-2 lg:gap-3 h-64 lg:h-80 px-2">
                                        {moduleProgress.length > 0 ? (
                                            moduleProgress.map((module, index) => {
                                                const height = module.progressPercentage > 0 ? `${Math.max(module.progressPercentage, 8)}%` : '8%';
                                                const isCompleted = module.progressPercentage === 100;
                                                const bgClass = isCompleted 
                                                    ? 'bg-gradient-to-t from-green-600 via-green-500 to-green-400' 
                                                    : module.progressPercentage > 0 
                                                        ? 'bg-gradient-to-t from-blue-600 via-blue-500 to-blue-400' 
                                                        : 'bg-gradient-to-t from-gray-300 to-gray-200';
                                                
                                                return (
                                                    <div key={module.moduleId || index} className="flex flex-col items-center flex-1 group">
                                                        <div className={`w-full max-w-14 lg:max-w-16 ${bgClass} rounded-t-lg relative shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer`} style={{height}}>
                                                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs lg:text-sm font-bold text-gray-800 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity bg-white px-2 py-1 rounded shadow-md">
                                                                {module.progressPercentage}%
                                                            </div>
                                                            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs lg:text-sm font-bold text-gray-800">
                                                                {module.progressPercentage}%
                                                            </div>
                                                        </div>
                                                        <div className="text-xs lg:text-sm text-gray-700 mt-3 text-center leading-tight font-semibold px-1">
                                                            {module.title}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            
                                            [1, 2, 3, 4, 5].map(num => (
                                                <div key={num} className="flex flex-col items-center flex-1">
                                                    <div className="w-full max-w-14 lg:max-w-16 bg-gray-200 rounded-t-lg relative" style={{height: '8%'}}>
                                                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs lg:text-sm font-bold text-gray-800">0%</div>
                                                    </div>
                                                    <div className="text-xs text-gray-600 mt-3 text-center leading-tight font-medium">Loading...</div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Module Progress */}
                    <div className="space-y-3 lg:space-y-4">
                        <div className="flex items-center justify-between mb-2 lg:mb-4">
                            <h2 className="text-base lg:text-lg font-semibold text-gray-700">Module Progress</h2>
                        </div>

                        {moduleProgress.length > 0 ? (
                            (showAllModules ? moduleProgress : moduleProgress.slice(0, 6)).map((module, index) => {
                                const isCompleted = module.progressPercentage === 100;
                                const isInProgress = module.progressPercentage > 0 && module.progressPercentage < 100;
                                
                                return (
                                    <div key={module.moduleId} className="bg-white rounded-xl p-4 shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
                                        <div className="mb-2">
                                            <div className="text-sm lg:text-base font-semibold text-gray-800 mb-1">{module.title}</div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-gray-600">Complete</span>
                                                <span className="text-xs font-bold text-gray-700">{module.progressPercentage}%</span>
                                            </div>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                            <div 
                                                className={`h-full rounded-full transition-all duration-500 ${
                                                    isCompleted 
                                                        ? 'bg-primary' 
                                                        : isInProgress 
                                                            ? 'bg-primary' 
                                                            : 'bg-gray-300'
                                                }`}
                                                style={{ width: `${module.progressPercentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            [1, 2, 3].map(num => (
                                <div key={num} className="bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm">
                                    <div className="mb-2">
                                        <div className="text-sm lg:text-base font-semibold text-gray-800 mb-1">Loading...</div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-600">0% Complete</span>
                                            <span className="text-xs font-bold text-gray-700">0%</span>
                                        </div>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-3">
                                        <div className="h-full rounded-full bg-gray-300" style={{ width: '0%' }}></div>
                                    </div>
                                </div>
                            ))
                        )}

                        {moduleProgress.length > 6 && (
                            <div className="text-center pt-2">
                                <button 
                                    onClick={() => setShowAllModules(!showAllModules)}
                                    className="text-primary font-semibold text-sm inline-flex items-center gap-2 hover:underline hover:gap-3 transition-all"
                                >
                                    {showAllModules ? 'Show Less' : 'See All'}
                                    <svg 
                                        className={`w-4 h-4 transition-transform ${showAllModules ? 'rotate-180' : ''}`} 
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DashboardContent;
